import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  Camera,
  Users,
  Bell,
  TrendingDown,
  Zap,
  Heart,
  CheckCircle,
  Sparkles,
  AlertTriangle,
} from 'lucide-react';

const HowToUse: React.FC = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('getting-started');

  const sections = [
    { id: 'getting-started', label: '🚀 Getting Started', icon: Zap },
    { id: 'adding-items', label: '📦 Adding Items', icon: Plus },
    { id: 'photos', label: '📸 Photo Upload', icon: Camera },
    { id: 'nutrition', label: '🥗 Nutrition & Allergens', icon: Heart },
    { id: 'categories', label: '🏷️ Custom Categories', icon: Plus },
    { id: 'cook-before-expires', label: '👨‍🍳 Cook Before Expires', icon: Sparkles },
    { id: 'gamification', label: '🏆 Badges & Achievements', icon: Sparkles },
    { id: 'color-guide', label: '🎨 Color Guide', icon: AlertTriangle },
    { id: 'scanning', label: '📱 Barcode Scanning', icon: Camera },
    { id: 'household', label: '👥 Household Sharing', icon: Users },
    { id: 'notifications', label: '🔔 Notifications', icon: Bell },
    { id: 'analytics', label: '📊 Analytics', icon: TrendingDown },
    { id: 'tips', label: '💡 Pro Tips', icon: Sparkles },
  ];

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(sectionId);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-2">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-gray-600 hover:text-gray-900 flex-shrink-0"
            >
              <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
              <span className="text-sm sm:text-base">Back</span>
            </button>
            <h1 className="text-base sm:text-xl md:text-2xl font-bold text-gray-900 text-center flex-1 min-w-0 truncate">
              🥬 How to Use FridgeLife
            </h1>
            <div className="w-12 sm:w-20 flex-shrink-0"></div>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-green-500 to-green-600 text-white py-6 sm:py-8 md:py-12">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4">
            Welcome to FridgeLife! 🎉
          </h2>
          <p className="text-sm sm:text-base md:text-lg lg:text-xl text-green-100 mb-4 sm:mb-6 max-w-2xl mx-auto">
            Your journey to zero food waste starts here.
            Let's make saving food (and money) fun! 💰🌱
          </p>
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3 md:gap-4 text-xs sm:text-sm">
            <div className="bg-white/20 rounded-full px-3 sm:px-4 md:px-6 py-1.5 sm:py-2 whitespace-nowrap">
              ⏱️ Takes 2 minutes
            </div>
            <div className="bg-white/20 rounded-full px-3 sm:px-4 md:px-6 py-1.5 sm:py-2 whitespace-nowrap">
              🎯 Save up to $3,000/year
            </div>
            <div className="bg-white/20 rounded-full px-3 sm:px-4 md:px-6 py-1.5 sm:py-2 whitespace-nowrap">
              🌍 Help save the planet
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-4 sticky top-24">
              <h3 className="font-bold text-gray-900 mb-4">Quick Jump</h3>
              <nav className="space-y-2">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg
                      transition-colors ${
                      activeSection === section.id
                        ? 'bg-green-100 text-green-700 font-medium'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {section.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-8">
            {/* Getting Started */}
            <section id="getting-started" 
              className="bg-white rounded-lg shadow-sm p-8">
              <div className="flex items-center mb-6">
                <div className="bg-green-100 p-3 rounded-full mr-4">
                  <Zap className="w-6 h-6 text-green-600" />
                </div>
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">
                  🚀 Getting Started
                </h2>
              </div>

              <div className="space-y-6">
                <div className="border-l-4 border-green-500 pl-4">
                  <h3 className="font-bold text-lg sm:text-xl mb-2">
                    Step 1: Sign Up (30 seconds)
                  </h3>
                  <p className="text-gray-600 mb-3">
                    Create your account with just an email and password. 
                    Boom! You're in. 🎊
                  </p>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-sm text-green-800">
                      <strong>Pro Tip:</strong> Your household is 
                      automatically created - no extra setup needed!
                    </p>
                  </div>
                </div>

                <div className="border-l-4 border-blue-500 pl-4">
                  <h3 className="font-bold text-lg sm:text-xl mb-2">
                    Step 2: Add Your First Item (1 minute)
                  </h3>
                  <p className="text-gray-600 mb-3">
                    Click the big green "Add Item" button. It's hard to miss! 😄
                  </p>
                  <ul className="list-disc list-inside text-gray-600 space-y-1">
                    <li>Name your item (e.g., "Organic Milk")</li>
                    <li>Pick where it lives: Fridge 🥬, Freezer 🧊, or Pantry 📦</li>
                    <li>Set the expiration date</li>
                    <li>Add quantity & price (optional but helpful!)</li>
                  </ul>
                </div>

                <div className="border-l-4 border-purple-500 pl-4">
                  <h3 className="font-bold text-lg sm:text-xl mb-2">
                    Step 3: Let the Magic Happen ✨
                  </h3>
                  <p className="text-gray-600">
                    We'll remind you 2 days before things expire. 
                    No more forgotten leftovers! 🎉
                  </p>
                </div>
              </div>
            </section>

            {/* Adding Items */}
            <section id="adding-items" 
              className="bg-white rounded-lg shadow-sm p-8">
              <div className="flex items-center mb-6">
                <div className="bg-blue-100 p-3 rounded-full mr-4">
                  <Plus className="w-6 h-6 text-blue-600" />
                </div>
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">
                  📦 Adding Items
                </h2>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="border-2 border-green-200 rounded-lg p-6 
                  hover:border-green-400 transition-colors">
                  <div className="text-center mb-4">
                    <div className="inline-block bg-green-100 p-4 
                      rounded-full mb-3">
                      <Plus className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="font-bold text-lg mb-2">Manual Entry</h3>
                    <p className="text-gray-600 text-sm">
                      Type it in yourself - perfect for 
                      homemade meals & leftovers!
                    </p>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2 
                        flex-shrink-0 mt-0.5" />
                      Great for custom items
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2 
                        flex-shrink-0 mt-0.5" />
                      Add price & quantity easily
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2 
                        flex-shrink-0 mt-0.5" />
                      No limits on creativity!
                    </li>
                  </ul>
                </div>

                <div className="border-2 border-blue-200 rounded-lg p-6 
                  hover:border-blue-400 transition-colors">
                  <div className="text-center mb-4">
                    <div className="inline-block bg-blue-100 p-4 
                      rounded-full mb-3">
                      <Camera className="w-8 h-8 text-blue-600" />
                    </div>
                    <h3 className="font-bold text-lg mb-2">
                      Barcode Scanning
                    </h3>
                    <p className="text-gray-600 text-sm">
                      Point, scan, done! Auto-fills everything for you. 
                      Like magic! ✨
                    </p>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-blue-500 mr-2 
                        flex-shrink-0 mt-0.5" />
                      Super fast & accurate
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-blue-500 mr-2 
                        flex-shrink-0 mt-0.5" />
                      Works with most products
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-blue-500 mr-2 
                        flex-shrink-0 mt-0.5" />
                      5 scans/month on free plan
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                <h4 className="font-bold text-yellow-900 mb-2">
                  🏷️ Categories Explained
                </h4>
                <div className="grid md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="font-bold text-gray-900">🥬 Fridge:</span>
                    <p className="text-gray-600">
                      Dairy, fresh produce, leftovers, drinks
                    </p>
                  </div>
                  <div>
                    <span className="font-bold text-gray-900">🧊 Freezer:</span>
                    <p className="text-gray-600">
                      Frozen foods, ice cream, meats
                    </p>
                  </div>
                  <div>
                    <span className="font-bold text-gray-900">📦 Pantry:</span>
                    <p className="text-gray-600">
                      Canned goods, pasta, snacks, spices
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Photo Upload */}
            <section id="photos" className="bg-white rounded-lg shadow-sm p-8">
              <div className="flex items-center mb-6">
                <div className="bg-purple-100 p-3 rounded-full mr-4">
                  <Camera className="w-6 h-6 text-purple-600" />
                </div>
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">📸 Photo Upload</h2>
              </div>
              <p className="text-gray-600 mb-6">Add photos to your items for easy visual identification - perfect for leftovers!</p>

              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-bold text-gray-900 mb-2">How to Add Photos:</h3>
                  <ol className="list-decimal list-inside space-y-2 text-gray-600">
                    <li>When adding or editing an item, look for the "Add a photo" section</li>
                    <li>Choose "Take Photo" to use your camera OR "From Gallery" to select existing photos</li>
                    <li>Photos are automatically compressed to save storage space</li>
                    <li>Photos display on Dashboard instead of category emojis</li>
                  </ol>
                </div>
                <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
                  <p className="text-sm text-blue-900">
                    <strong>💡 Pro Tip:</strong> Use photos for leftovers, opened packages, and partial items to quickly identify what you have!
                  </p>
                </div>
              </div>
            </section>

            {/* Nutrition & Allergens */}
            <section id="nutrition" className="bg-white rounded-lg shadow-sm p-8">
              <div className="flex items-center mb-6">
                <div className="bg-green-100 p-3 rounded-full mr-4">
                  <Heart className="w-6 h-6 text-green-600" />
                </div>
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">🥗 Nutrition & Allergens</h2>
              </div>
              <p className="text-gray-600 mb-6">Automatically capture nutrition facts and allergen warnings when you scan barcodes!</p>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-green-50 rounded-lg p-6">
                  <h3 className="font-bold text-gray-900 mb-3">Nutrition Data</h3>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      Calories (per 100g)
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      Protein, Carbs, Fat
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      Fiber & Sodium
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      Displayed on item cards
                    </li>
                  </ul>
                </div>
                <div className="bg-red-50 rounded-lg p-6">
                  <h3 className="font-bold text-gray-900 mb-3">Allergen Warnings ⚠️</h3>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                      Auto-detected from products
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                      Prominent red warnings
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                      Milk, Eggs, Nuts, etc.
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                      Keeps your family safe
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Custom Categories */}
            <section id="categories" className="bg-white rounded-lg shadow-sm p-8">
              <div className="flex items-center mb-6">
                <div className="bg-indigo-100 p-3 rounded-full mr-4">
                  <Plus className="w-6 h-6 text-indigo-600" />
                </div>
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">🏷️ Custom Categories</h2>
              </div>
              <p className="text-gray-600 mb-6">Go beyond Fridge, Freezer, and Pantry with your own custom categories!</p>

              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-bold text-gray-900 mb-2">How to Create Custom Categories:</h3>
                  <ol className="list-decimal list-inside space-y-2 text-gray-600">
                    <li>Go to Household Settings from the Dashboard</li>
                    <li>Scroll to "Custom Categories" section</li>
                    <li>Type your category name (e.g., "Kid's Snacks", "Meal Prep")</li>
                    <li>Click "Add" - it's now available when adding items!</li>
                  </ol>
                </div>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="font-bold text-blue-900">Examples:</p>
                    <ul className="text-sm text-blue-800 mt-2 space-y-1">
                      <li>• Kid's Snacks</li>
                      <li>• Meal Prep</li>
                      <li>• Work Lunch</li>
                    </ul>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="font-bold text-green-900">More Ideas:</p>
                    <ul className="text-sm text-green-800 mt-2 space-y-1">
                      <li>• Leftovers</li>
                      <li>• Baking Supplies</li>
                      <li>• Breakfast Items</li>
                    </ul>
                  </div>
                  <div className="bg-yellow-50 p-4 rounded-lg border-2 border-yellow-400">
                    <p className="font-bold text-yellow-900">Free vs Premium:</p>
                    <p className="text-sm text-yellow-800 mt-2">
                      Free: 3 categories<br/>
                      Premium: Unlimited!
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Cook Before It Expires */}
            <section id="cook-before-expires" className="bg-white rounded-lg shadow-sm p-8">
              <div className="flex items-center mb-6">
                <div className="bg-orange-100 p-3 rounded-full mr-4">
                  <Sparkles className="w-6 h-6 text-orange-600" />
                </div>
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">👨‍🍳 Cook Before It Expires</h2>
              </div>
              <p className="text-gray-600 mb-6">Get smart meal suggestions based on items expiring in the next 5 days!</p>

              <div className="space-y-4">
                <div className="bg-blue-50 rounded-lg p-4 mb-4">
                  <h3 className="font-bold text-blue-900 mb-2">🔍 How to Access:</h3>
                  <p className="text-blue-800 text-sm mb-2">
                    Look for the <strong>👨‍🍳 "Cook"</strong> button in your Dashboard navigation (top right, next to Analytics)
                  </p>
                  <ul className="text-sm text-blue-700 space-y-1 ml-4">
                    <li>• Click the ChefHat icon + "Cook" text</li>
                    <li>• Takes you to the Cook Before It Expires page</li>
                    <li>• Shows items expiring in the next 5 days</li>
                    <li>• Get smart meal suggestions instantly!</li>
                  </ul>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-bold text-gray-900 mb-2">How It Works:</h3>
                  <ol className="list-decimal list-inside space-y-2 text-gray-600">
                    <li>Click the 👨‍🍳 "Cook" button in Dashboard navigation</li>
                    <li>See all items expiring in next 5 days</li>
                    <li>Select items you want to use</li>
                    <li>Get smart meal suggestions (Stir Fry, Soup, Pasta, Salad, Omelette)</li>
                    <li>Click "Search Recipes" to find recipes on Google</li>
                    <li>Mark items as "Used" when done cooking!</li>
                  </ol>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h3 className="font-bold text-green-900 mb-2">Meal Suggestions Include:</h3>
                    <ul className="text-sm text-green-800 space-y-1">
                      <li>🍜 Stir Fry - protein + veggies</li>
                      <li>🍲 Soup - 3+ veggies or broth</li>
                      <li>🍝 Pasta Dish - pasta + sauce</li>
                      <li>🥗 Salad - greens + toppings</li>
                      <li>🍳 Omelette - eggs + ingredients</li>
                    </ul>
                  </div>
                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                    <p className="text-sm text-yellow-900">
                      <strong>⭐ Premium Feature:</strong> Full meal planning calendar with drag-and-drop coming soon!
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Gamification */}
            <section id="gamification" className="bg-white rounded-lg shadow-sm p-8">
              <div className="flex items-center mb-6">
                <div className="bg-yellow-100 p-3 rounded-full mr-4">
                  <Sparkles className="w-6 h-6 text-yellow-600" />
                </div>
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">🏆 Badges & Achievements</h2>
              </div>
              <p className="text-gray-600 mb-6">Earn badges, track streaks, and see your community impact!</p>

              <div className="grid md:grid-cols-3 gap-6 mb-6">
                <div className="bg-gray-100 rounded-lg p-6">
                  <h3 className="font-bold text-gray-900 mb-3">Common Badges</h3>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li>🌱 First Save (1 item)</li>
                    <li>⚔️ Waste Warrior (10 items)</li>
                    <li>📱 Scanner (5 scans)</li>
                  </ul>
                </div>
                <div className="bg-blue-100 rounded-lg p-6">
                  <h3 className="font-bold text-blue-900 mb-3">Rare Badges</h3>
                  <ul className="space-y-2 text-sm text-blue-800">
                    <li>🏆 Zero Waste Week</li>
                    <li>💚 Food Saver (50 items)</li>
                    <li>🔍 Scan Master (50 scans)</li>
                  </ul>
                </div>
                <div className="bg-purple-100 rounded-lg p-6">
                  <h3 className="font-bold text-purple-900 mb-3">Epic & Legendary</h3>
                  <ul className="space-y-2 text-sm text-purple-800">
                    <li>💰 Money Saver ($100)</li>
                    <li>🌍 Eco Warrior (100 items)</li>
                    <li>👑 Zero Waste Champion (30 days)</li>
                  </ul>
                </div>
              </div>

              <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-6">
                <h3 className="font-bold text-gray-900 mb-3">Community Stats 🌍</h3>
                <p className="text-gray-600 mb-3">See the global impact of all FridgeLife users combined:</p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>📈 Total items saved by all users</li>
                  <li>🌱 Total CO₂ emissions prevented</li>
                  <li>💰 Total money saved across community</li>
                  <li>🔥 Your personal zero-waste streak</li>
                </ul>
              </div>
            </section>

            {/* Color Guide */}
            <section id="color-guide"
              className="bg-white rounded-lg shadow-sm p-8">
              <div className="flex items-center mb-6">
                <div className="bg-gradient-to-r from-red-100 to-green-100 p-3 rounded-full mr-4">
                  <AlertTriangle className="w-6 h-6 text-orange-600" />
                </div>
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">
                  🎨 Understanding Color Codes
                </h2>
              </div>

              <p className="text-gray-600 mb-6 text-lg">
                FridgeLife uses color-coding to help you instantly see which items need attention.
                Here's what each color means:
              </p>

              <div className="space-y-4 mb-8">
                {/* Expired - Red */}
                <div className="border-l-4 border-red-500 bg-red-50 p-5 rounded-r-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <div className="w-6 h-6 bg-red-500 rounded-full mr-3"></div>
                      <h3 className="font-bold text-base sm:text-lg md:text-xl text-red-900">EXPIRED</h3>
                    </div>
                    <span className="text-red-700 font-semibold">Passed expiration date</span>
                  </div>
                  <p className="text-red-800 ml-9">
                    Items that have already expired. Check if they're still safe to use or dispose of them properly.
                  </p>
                </div>

                {/* Critical - Orange/Red */}
                <div className="border-l-4 border-orange-500 bg-orange-50 p-5 rounded-r-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <div className="w-6 h-6 bg-orange-500 rounded-full mr-3"></div>
                      <h3 className="font-bold text-base sm:text-lg md:text-xl text-orange-900">CRITICAL</h3>
                    </div>
                    <span className="text-orange-700 font-semibold">0-2 days left</span>
                  </div>
                  <p className="text-orange-800 ml-9">
                    Use these items ASAP! They expire in 2 days or less. Perfect for tonight's dinner!
                  </p>
                </div>

                {/* Warning - Orange */}
                <div className="border-l-4 border-orange-400 bg-orange-50/60 p-5 rounded-r-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <div className="w-6 h-6 bg-orange-400 rounded-full mr-3"></div>
                      <h3 className="font-bold text-base sm:text-lg md:text-xl text-orange-800">WARNING</h3>
                    </div>
                    <span className="text-orange-600 font-semibold">3-5 days left</span>
                  </div>
                  <p className="text-orange-700 ml-9">
                    Start planning to use these items. They're expiring soon but you still have a few days.
                  </p>
                </div>

                {/* Caution - Yellow */}
                <div className="border-l-4 border-yellow-500 bg-yellow-50 p-5 rounded-r-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <div className="w-6 h-6 bg-yellow-500 rounded-full mr-3"></div>
                      <h3 className="font-bold text-base sm:text-lg md:text-xl text-yellow-900">CAUTION</h3>
                    </div>
                    <span className="text-yellow-700 font-semibold">6-7 days left</span>
                  </div>
                  <p className="text-yellow-800 ml-9">
                    Keep these items on your radar. They'll expire within a week.
                  </p>
                </div>

                {/* Safe - Green */}
                <div className="border-l-4 border-green-500 bg-green-50 p-5 rounded-r-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <div className="w-6 h-6 bg-green-500 rounded-full mr-3"></div>
                      <h3 className="font-bold text-base sm:text-lg md:text-xl text-green-900">SAFE</h3>
                    </div>
                    <span className="text-green-700 font-semibold">8+ days left</span>
                  </div>
                  <p className="text-green-800 ml-9">
                    All good! These items are fresh and have plenty of time before expiration.
                  </p>
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg">
                <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                  <Zap className="w-5 h-5 text-blue-600 mr-2" />
                  Quick Sorting Tips:
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-start">
                    <span className="text-xl sm:text-2xl mr-2">🔴</span>
                    <div>
                      <p className="font-semibold text-gray-900">Urgency Sort</p>
                      <p className="text-sm text-gray-600">
                        Default view - shows most urgent items first (red → green)
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <span className="text-xl sm:text-2xl mr-2">📅</span>
                    <div>
                      <p className="font-semibold text-gray-900">Date Added Sort</p>
                      <p className="text-sm text-gray-600">
                        Click the sort button to view newest items first
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mt-6">
                <p className="text-sm text-yellow-900">
                  <strong>💡 Pro Tip:</strong> Check your dashboard daily to see color-coded items.
                  Plan meals around orange and red items to minimize waste!
                </p>
              </div>
            </section>

            {/* Barcode Scanning */}
            <section id="scanning" 
              className="bg-white rounded-lg shadow-sm p-8">
              <div className="flex items-center mb-6">
                <div className="bg-purple-100 p-3 rounded-full mr-4">
                  <Camera className="w-6 h-6 text-purple-600" />
                </div>
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">
                  📸 Barcode Scanning Magic
                </h2>
              </div>

              <div className="mb-6">
                <h3 className="font-bold text-lg sm:text-xl mb-4">How to Scan:</h3>
                <div className="grid md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl sm:text-3xl mb-2">1️⃣</div>
                    <p className="text-sm font-medium">Click "Scan Barcode"</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl sm:text-3xl mb-2">2️⃣</div>
                    <p className="text-sm font-medium">
                      Allow camera access
                    </p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl sm:text-3xl mb-2">3️⃣</div>
                    <p className="text-sm font-medium">
                      Point at barcode
                    </p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl sm:text-3xl mb-2">4️⃣</div>
                    <p className="text-sm font-medium">Done! ✨</p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 p-6 rounded-lg">
                <h4 className="font-bold text-blue-900 mb-3">
                  📱 Scanning Tips for Success:
                </h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">💡</span>
                    Good lighting helps! Natural light is best.
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">💡</span>
                    Keep the barcode flat and straight.
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">💡</span>
                    Not too close, not too far - about 6 inches away.
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">💡</span>
                    If it doesn't work, try manual entry instead!
                  </li>
                </ul>
              </div>
            </section>

            {/* Household Sharing */}
            <section id="household" 
              className="bg-white rounded-lg shadow-sm p-8">
              <div className="flex items-center mb-6">
                <div className="bg-pink-100 p-3 rounded-full mr-4">
                  <Users className="w-6 h-6 text-pink-600" />
                </div>
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">
                  👥 Household Sharing
                </h2>
              </div>

              <p className="text-gray-600 mb-6 text-lg">
                FridgeLife is better together! Share your inventory with
                family, roommates, or anyone in your household. 👨‍👩‍👧‍👦
              </p>

              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="border-2 border-green-200 rounded-lg p-6
                    hover:border-green-400 transition-colors">
                    <div className="text-center mb-4">
                      <div className="inline-block bg-green-100 p-4
                        rounded-full mb-3">
                        <span className="text-2xl sm:text-3xl">🔑</span>
                      </div>
                      <h3 className="font-bold text-lg mb-2">Quick Join Code</h3>
                      <p className="text-gray-600 text-sm">
                        The fastest way to add household members!
                      </p>
                    </div>
                    <ol className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-start">
                        <span className="font-bold text-green-600 mr-2">1.</span>
                        Go to Household Settings
                      </li>
                      <li className="flex items-start">
                        <span className="font-bold text-green-600 mr-2">2.</span>
                        Copy your unique join code (e.g., ABC-123)
                      </li>
                      <li className="flex items-start">
                        <span className="font-bold text-green-600 mr-2">3.</span>
                        Share it with family/roommates
                      </li>
                      <li className="flex items-start">
                        <span className="font-bold text-green-600 mr-2">4.</span>
                        They enter the code on the Join page
                      </li>
                      <li className="flex items-start">
                        <span className="font-bold text-green-600 mr-2">5.</span>
                        Boom - instant shared fridge! 🎉
                      </li>
                    </ol>
                  </div>

                  <div className="border-2 border-blue-200 rounded-lg p-6
                    hover:border-blue-400 transition-colors">
                    <div className="text-center mb-4">
                      <div className="inline-block bg-blue-100 p-4
                        rounded-full mb-3">
                        <span className="text-2xl sm:text-3xl">🔍</span>
                      </div>
                      <h3 className="font-bold text-lg mb-2">
                        Search & Add User
                      </h3>
                      <p className="text-gray-600 text-sm">
                        Add someone who already has a FridgeLife account
                      </p>
                    </div>
                    <ol className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-start">
                        <span className="font-bold text-blue-600 mr-2">1.</span>
                        Go to Household Settings
                      </li>
                      <li className="flex items-start">
                        <span className="font-bold text-blue-600 mr-2">2.</span>
                        Use "Add Existing User" section
                      </li>
                      <li className="flex items-start">
                        <span className="font-bold text-blue-600 mr-2">3.</span>
                        Search by their email address
                      </li>
                      <li className="flex items-start">
                        <span className="font-bold text-blue-600 mr-2">4.</span>
                        Click "Add to Household"
                      </li>
                      <li className="flex items-start">
                        <span className="font-bold text-blue-600 mr-2">5.</span>
                        They're instantly added! ⚡
                      </li>
                    </ol>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-pink-50 to-purple-50 
                  p-6 rounded-lg">
                  <h4 className="font-bold text-gray-900 mb-3">
                    What Everyone Can Do:
                  </h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="flex items-start">
                      <Heart className="w-5 h-5 text-pink-500 mr-2 
                        flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">
                        Add, edit, or delete any item
                      </span>
                    </div>
                    <div className="flex items-start">
                      <Heart className="w-5 h-5 text-pink-500 mr-2 
                        flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">
                        See all household items
                      </span>
                    </div>
                    <div className="flex items-start">
                      <Heart className="w-5 h-5 text-pink-500 mr-2 
                        flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">
                        Get expiration notifications
                      </span>
                    </div>
                    <div className="flex items-start">
                      <Heart className="w-5 h-5 text-pink-500 mr-2 
                        flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">
                        View analytics together
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                  <p className="text-sm text-yellow-900">
                    <strong>Free Plan Limit:</strong> 2 members max. 
                    Upgrade to Premium for unlimited household members!
                  </p>
                </div>
              </div>
            </section>

            {/* Notifications */}
            <section id="notifications" 
              className="bg-white rounded-lg shadow-sm p-8">
              <div className="flex items-center mb-6">
                <div className="bg-orange-100 p-3 rounded-full mr-4">
                  <Bell className="w-6 h-6 text-orange-600" />
                </div>
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">
                  🔔 Never Miss an Expiration!
                </h2>
              </div>

              <div className="mb-6">
                <h3 className="font-bold text-lg sm:text-xl mb-3">
                  How Notifications Work:
                </h3>
                <div className="bg-gradient-to-r from-orange-50 to-red-50 
                  p-6 rounded-lg">
                  <div className="flex items-center mb-4">
                    <div className="bg-white rounded-full p-3 mr-4">
                      <span className="text-2xl">📅</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900">
                        2 Days Before Expiration
                      </h4>
                      <p className="text-gray-600">
                        We send you a friendly email reminder
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">
                    Example: If your milk expires on Friday, you'll get an 
                    email on Wednesday morning at 9 AM.
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="border-2 border-green-200 rounded-lg p-4 
                  flex items-start">
                  <span className="text-xl sm:text-2xl mr-2 sm:mr-3">✅</span>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">
                      Smart Grouping
                    </h4>
                    <p className="text-sm text-gray-600">
                      Multiple items expiring? We bundle them in one email 
                      so we don't spam you!
                    </p>
                  </div>
                </div>

                <div className="border-2 border-blue-200 rounded-lg p-4 
                  flex items-start">
                  <span className="text-xl sm:text-2xl mr-2 sm:mr-3">👨‍👩‍👧‍👦</span>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">
                      Everyone Gets Notified
                    </h4>
                    <p className="text-sm text-gray-600">
                      All household members receive the same notifications - 
                      teamwork makes the dream work!
                    </p>
                  </div>
                </div>

                <div className="border-2 border-purple-200 rounded-lg p-4 
                  flex items-start">
                  <span className="text-xl sm:text-2xl mr-2 sm:mr-3">💰</span>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">
                      See the Savings
                    </h4>
                    <p className="text-sm text-gray-600">
                      Each email shows you how much money you're saving by 
                      not wasting food!
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Analytics */}
            <section id="analytics" 
              className="bg-white rounded-lg shadow-sm p-8">
              <div className="flex items-center mb-6">
                <div className="bg-indigo-100 p-3 rounded-full mr-4">
                  <TrendingDown className="w-6 h-6 text-indigo-600" />
                </div>
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">
                  📊 Track Your Success
                </h2>
              </div>

              <p className="text-gray-600 mb-6 text-lg">
                See your impact! Your analytics dashboard shows you exactly 
                how much money you're saving and waste you're preventing. 🌟
              </p>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="bg-green-50 p-6 rounded-lg">
                  <div className="text-3xl mb-2">💰</div>
                  <h4 className="font-bold text-lg mb-2">Money Saved</h4>
                  <p className="text-gray-600">
                    Track every dollar saved by not throwing away food. 
                    Watch it add up!
                  </p>
                </div>

                <div className="bg-blue-50 p-6 rounded-lg">
                  <div className="text-3xl mb-2">📈</div>
                  <h4 className="font-bold text-lg mb-2">Usage Trends</h4>
                  <p className="text-gray-600">
                    See what you consume vs. what goes to waste. Learn your 
                    patterns!
                  </p>
                </div>

                <div className="bg-purple-50 p-6 rounded-lg">
                  <div className="text-3xl mb-2">🎯</div>
                  <h4 className="font-bold text-lg mb-2">
                    Category Breakdown
                  </h4>
                  <p className="text-gray-600">
                    Which category has the most items? Where's the waste 
                    coming from?
                  </p>
                </div>

                <div className="bg-green-50 p-6 rounded-lg">
                  <div className="text-3xl mb-2">🌍</div>
                  <h4 className="font-bold text-lg mb-2">
                    Environmental Impact
                  </h4>
                  <p className="text-gray-600">
                    See your CO₂ savings! Feel good about helping the planet.
                  </p>
                </div>
              </div>

              <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4">
                <p className="text-sm text-indigo-900">
                  <strong>Premium Feature:</strong> Advanced analytics with 
                  detailed trends, comparisons, and export options. 
                  Upgrade to unlock! 🚀
                </p>
              </div>
            </section>

            {/* Pro Tips */}
            <section id="tips" 
              className="bg-gradient-to-r from-yellow-50 to-orange-50 
              rounded-lg shadow-sm p-8">
              <div className="flex items-center mb-6">
                <div className="bg-yellow-100 p-3 rounded-full mr-4">
                  <Sparkles className="w-6 h-6 text-yellow-600" />
                </div>
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">
                  💡 Pro Tips & Tricks
                </h2>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <h4 className="font-bold text-lg mb-3">🎯 Best Practices</h4>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">•</span>
                      Add items as soon as you get home from shopping
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">•</span>
                      Be realistic with expiration dates - check the package!
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">•</span>
                      Mark items as "consumed" when you use them
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">•</span>
                      Check your dashboard weekly to plan meals
                    </li>
                  </ul>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <h4 className="font-bold text-lg mb-3">⚡ Time Savers</h4>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-start">
                      <span className="text-blue-500 mr-2">•</span>
                      Use barcode scanning for packaged items
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-500 mr-2">•</span>
                      Create templates for items you buy regularly
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-500 mr-2">•</span>
                      Filter by category when looking for specific items
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-500 mr-2">•</span>
                      Sort by "expiring soon" to prioritize meal planning
                    </li>
                  </ul>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <h4 className="font-bold text-lg mb-3">
                    🏠 Household Harmony
                  </h4>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-start">
                      <span className="text-purple-500 mr-2">•</span>
                      Assign each person their favorite categories
                    </li>
                    <li className="flex items-start">
                      <span className="text-purple-500 mr-2">•</span>
                      Have a weekly "fridge clean-out" ritual
                    </li>
                    <li className="flex items-start">
                      <span className="text-purple-500 mr-2">•</span>
                      Celebrate your household's waste reduction together!
                    </li>
                    <li className="flex items-start">
                      <span className="text-purple-500 mr-2">•</span>
                      Make it a game - who can save the most?
                    </li>
                  </ul>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <h4 className="font-bold text-lg mb-3">🌟 Level Up</h4>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-start">
                      <span className="text-orange-500 mr-2">•</span>
                      Upgrade to Premium for unlimited everything
                    </li>
                    <li className="flex items-start">
                      <span className="text-orange-500 mr-2">•</span>
                      Use advanced analytics to spot patterns
                    </li>
                    <li className="flex items-start">
                      <span className="text-orange-500 mr-2">•</span>
                      Export your data for tax deductions
                    </li>
                    <li className="flex items-start">
                      <span className="text-orange-500 mr-2">•</span>
                      Share your success story with friends!
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-green-500 to-green-600
              rounded-lg shadow-lg p-6 sm:p-8 text-white text-center">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4">
                Ready to Save Food & Money? 🎉
              </h2>
              <p className="text-base sm:text-lg md:text-xl text-green-100 mb-4 sm:mb-6">
                You've got this! Start tracking your first item today.
              </p>
              <button
                onClick={() => navigate('/add-item')}
                className="bg-white text-green-600 px-8 py-4 rounded-lg 
                  font-bold text-lg hover:bg-green-50 transition-colors 
                  shadow-lg"
              >
                Add Your First Item →
              </button>
            </div>

            {/* Need Help */}
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <h3 className="font-bold text-xl mb-3">Still Have Questions?</h3>
              <p className="text-gray-600 mb-4">
                We're here to help! Check out our other resources:
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <button
                  onClick={() => navigate('/privacy')}
                  className="text-green-600 hover:text-green-700 
                    font-medium underline"
                >
                  Privacy Policy
                </button>
                <button
                  onClick={() => navigate('/terms')}
                  className="text-green-600 hover:text-green-700 
                    font-medium underline"
                >
                  Terms & Conditions
                </button>
                <a
                  href="mailto:fridgelife@yahoo.com"
                  className="text-green-600 hover:text-green-700 
                    font-medium underline"
                >
                  Contact Support
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-800 text-white py-8 mt-12">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-gray-400">
            Made with 💚 by FridgeLife • 
            Saving food, one fridge at a time 🥬
          </p>
        </div>
      </div>
    </div>
  );
};

export default HowToUse;
